import { QRCodeDetailsFeedstockForm } from '../../pages/QRCodeDetails/components/QRCodeDetailsFeedstockFormModal/qrcode-details-feedstock-form-modal.types'
import { QRCodeDetailsPartForm } from '../../pages/QRCodeDetails/components/QRCodeDetailsPartFormModal/qrcode-details-part-form-modal.types'
import { QRCodeDetailsProductForm } from '../../pages/QRCodeDetails/components/QRCodeDetailsProductFormModal/qrcode-details-product-form-modal.types'
import { IError } from '../base/error'
import ServiceBase from '../base/service.base'

import {
  IFinishedItemCategory,
  IFinishedItemProfileMetric,
  IFinishedItemProfileStatisticsResponse,
  IFinishedItemProfileUploadResponse,
  IGetFinishedItemProfiles,
  IGetItemTraceabilityStepsResponse,
  IItemWithFinishedItemProfile,
  ISaveItemTraceabilityStepDTO,
} from './finished-item-profile.types'

export class FinishedItemProfile extends ServiceBase {
  public async getFinishedItemProfiles(
    page: number,
    filter?: string,
    brandId?: string,
  ): Promise<IGetFinishedItemProfiles> {
    const url = `/finished-item-profile`

    const response = await this.request.get<IGetFinishedItemProfiles, IError>(
      url,
      {
        params: {
          page: page,
          limit: 8,
          filter: filter || undefined,
          brandId: brandId === 'all' ? undefined : brandId,
        },
      },
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getFinishedItemProfile(
    code: string,
  ): Promise<IItemWithFinishedItemProfile> {
    const url = `/finished-item-profile/item/${code}`

    const response = await this.request.get<
      IItemWithFinishedItemProfile,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async saveFinishedItemProfile(
    code: string,
    data: Partial<QRCodeDetailsProductForm>,
  ): Promise<void> {
    const url = `/finished-item-profile/${code}`

    const body = { ...data }

    delete body.images
    const response = await this.request.post<void, IError>(url, {
      ...body,
      qrcodeAccess: data?.qrcodeAccess === 'active' ? true : false,
    })

    if (response.status !== 201 && response.status !== 200) {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async saveFinishedItemProfilePart(
    code: string,
    data: QRCodeDetailsPartForm,
  ): Promise<void> {
    const url = `/finished-item-profile/${code}/part`

    const response = await this.request.post<void, IError>(url, data)

    if (response.status !== 201 && response.status !== 200) {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async deleteFinishedItemProfilePart(id: string): Promise<void> {
    const url = `/finished-item-profile/part/${id}`

    const response = await this.request.delete<void, IError>(url)
    if (response.status !== 201 && response.status !== 200) {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
    return
  }

  public async saveFeedstockItemProfile(
    id: string,
    data: QRCodeDetailsFeedstockForm,
  ): Promise<void> {
    const url = `/finished-item-profile/part/${id}/feedstock`

    const response = await this.request.post<void, IError>(url, {
      ...data,
      composition: parseInt(data.composition),
    })

    if (response.status !== 201 && response.status !== 200) {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async deleteFeedstockItemProfile(id: string): Promise<void> {
    const url = `/finished-item-profile/feedstock/${id}`

    const response = await this.request.delete<void, IError>(url)
    if (response.status !== 201 && response.status !== 200) {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
    return
  }

  public async getCategories(): Promise<IFinishedItemCategory[]> {
    const url = `/finished-item-profile/categories`

    const response = await this.request.get<IFinishedItemCategory[], IError>(
      url,
    )

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async uploadFinishedItemProfileImage(
    code: string,
    body: FormData,
  ): Promise<IFinishedItemProfileUploadResponse> {
    const url = `/finished-item-profile/${code}/image`

    const response = await this.request.post<
      IFinishedItemProfileUploadResponse,
      IError
    >(url, body)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async updateFinishedItemProfileMetrics(
    code: string,
    metrics: IFinishedItemProfileMetric[],
  ): Promise<void> {
    const url = `/finished-item-profile/${code}/metrics`

    await this.request.post<IFinishedItemProfileUploadResponse, IError>(url, {
      metrics,
    })
  }

  public async deleteFinishedItemProfileImage(
    code: string,
    image: string,
  ): Promise<void> {
    const url = `/finished-item-profile/${code}/image`

    await this.request.delete<
      {
        url: string
      },
      IError
    >(url, {
      data: {
        url: image,
      },
    })
  }

  public async getStatistics(): Promise<IFinishedItemProfileStatisticsResponse> {
    const url = `/finished-item-profile/statistics`

    const response = await this.request.get<
      IFinishedItemProfileStatisticsResponse,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async getItemTraceabilitySteps(code: string) {
    const url = `/item/traceability/qrcode/steps/${code}`

    const response = await this.request.get<
      IGetItemTraceabilityStepsResponse,
      IError
    >(url)

    if (response.result) {
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async saveItemTraceabilityStep(body: ISaveItemTraceabilityStepDTO) {
    const url = `/item/traceability/step`

    const response = await this.request.put<
      ISaveItemTraceabilityStepDTO,
      IError
    >(url, body)

    if (response.status !== 201 && response.status !== 200) {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }

  public async createItemTraceabilityStepImage(
    title: string,
    itemCode: string,
    file: File,
  ) {
    const url = `/item/traceability/step/assets`

    const body = new FormData()
    body.append('file', file)
    body.append('title', title)
    body.append('itemCode', itemCode)

    const response = await this.request.post<any, IError>(url, body)

    if (response.result) {
      console.log(response)
      return response.result
    } else {
      const error = response.error as IError
      throw new IError(error.statusCode, error.message, error.error)
    }
  }
}
